import { count } from './format.js'

/**
 * Previous and next, and where you are.
 *
 * Four screens page, and this was written inside the first of them. The
 * arithmetic is the part worth having in one place: the last page is a ceiling
 * division, the range is one-based, and both are easy to get subtly wrong in a
 * way that only shows on the final page of a list nobody scrolls to.
 *
 * Renders nothing when everything fits on one page — controls that can only be
 * disabled are furniture.
 */
export function Pager({
  page,
  pageSize,
  total,
  onPage,
}: {
  readonly page: number
  readonly pageSize: number
  readonly total: number
  readonly onPage: (page: number) => void
}) {
  const lastPage = Math.max(1, Math.ceil(total / pageSize))
  if (lastPage === 1) return null

  const first = (page - 1) * pageSize + 1
  const last = Math.min(page * pageSize, total)

  return (
    <div className="mt-4 flex items-center justify-between">
      <p className="text-[12px] text-t3">
        <span className="font-num tabular-nums">
          {count(first)}–{count(last)}
        </span>{' '}
        of <span className="font-num tabular-nums">{count(total)}</span>
      </p>
      <div className="flex gap-2">
        <PagerButton disabled={page <= 1} onClick={() => onPage(page - 1)}>
          Previous
        </PagerButton>
        <PagerButton disabled={page >= lastPage} onClick={() => onPage(page + 1)}>
          Next
        </PagerButton>
      </div>
    </div>
  )
}

function PagerButton({
  disabled,
  onClick,
  children,
}: {
  readonly disabled: boolean
  readonly onClick: () => void
  readonly children: string
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="rounded-field border border-line bg-surface px-3 py-1.5 font-medium text-[13px] text-t2 hover:text-t1 disabled:cursor-not-allowed disabled:opacity-50"
    >
      {children}
    </button>
  )
}

/** A skeleton rather than a spinner: the shape of what is coming, not a wait. */
export function TableSkeleton() {
  return (
    <div className="overflow-hidden rounded-card border border-line bg-surface">
      {[0, 1, 2, 3, 4, 5].map((row) => (
        <div className="border-line-2 border-b px-4 py-3.5 last:border-0" key={row}>
          <div className="h-3.5 w-1/3 animate-pulse rounded bg-line-2" />
        </div>
      ))}
    </div>
  )
}
